/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Forecast } from '@futrli-sdk/predict';
import {
  getPredictForecastBalanceSheetUrl,
  getPredictForecastCostsUrl,
  getPredictForecastExpensesUrl,
  getPredictForecastImportUrl,
  getPredictForecastInvoicesUrl,
  getPredictForecastSalesUrl,
  getPredictNonFinancialDriversUrl
} from '@futrli/shared-services';

export enum ChecklistItemIDs {
  Drivers = 'drivers',
  Expenses = 'expenses',
  Costs = 'costs',
  Sales = 'sales',
  Settings = 'settings',
  AddStaffGoogleSheetsTemplate = 'add-staff-google-sheets-template',
  AddLoansGoogleSheetsTemplate = 'add-loans-google-sheets-template',
  BalanceSheet = 'balance-sheet',
  InvoicesBills = 'invoices-bills'
}

export const useCheckboxInfos = (forecast: Forecast) => {
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';

  return useMemo(() => {
    if (forecast?.kind === 'budget') {
      return [
        {
          label: (
            <FormattedMessage
              id="predict.add_any_drivers"
              defaultMessage="Add any Drivers required for manual predictions"
            />
          ),
          link: getPredictNonFinancialDriversUrl(workspaceId, organisationId),
          id: ChecklistItemIDs.Drivers
        },
        {
          label: <FormattedMessage id="predict.set_expenses" defaultMessage="Set Expenses" />,
          link: getPredictForecastExpensesUrl(workspaceId, organisationId, forecast?.id || ''),
          id: ChecklistItemIDs.Expenses
        },
        {
          label: <FormattedMessage id="predict.set_costs" defaultMessage="Set Costs" />,
          link: getPredictForecastCostsUrl(workspaceId, organisationId, forecast?.id || ''),
          id: ChecklistItemIDs.Costs
        },
        {
          label: <FormattedMessage id="predict.set_sales" defaultMessage="Set Sales to cover all outgoings" />,
          link: getPredictForecastSalesUrl(workspaceId, organisationId, forecast?.id || ''),
          id: ChecklistItemIDs.Sales
        }
      ];
    }
    const infos = [
      {
        label: (
          <FormattedMessage id="predict.check_forecast_settings" defaultMessage="Check forecast settings for VAT/GST" />
        ),
        link: '',
        id: ChecklistItemIDs.Settings,
        shouldOpenSettings: true
      },
      {
        label: (
          <FormattedMessage
            id="predict.add_any_drivers"
            defaultMessage="Add any Drivers required for manual predictions"
          />
        ),
        id: ChecklistItemIDs.Drivers,
        link: getPredictNonFinancialDriversUrl(workspaceId, organisationId)
      },
      {
        label: <FormattedMessage id="predict.forecast_expenses" defaultMessage="Forecast Expenses (excluding staff)" />,
        link: getPredictForecastExpensesUrl(workspaceId, organisationId, forecast?.id || ''),
        id: ChecklistItemIDs.Expenses
      },
      {
        label: <FormattedMessage id="predict.forecast_costs" defaultMessage="Forecast Costs (excluding staff)" />,
        link: getPredictForecastCostsUrl(workspaceId, organisationId, forecast?.id || ''),
        id: ChecklistItemIDs.Costs
      },
      {
        label: (
          <FormattedMessage
            id="predict.add_staff_google_sheet"
            defaultMessage="Add staff using Google Sheets template"
          />
        ),
        link: getPredictForecastImportUrl(workspaceId, organisationId, forecast?.id || ''),
        id: ChecklistItemIDs.AddStaffGoogleSheetsTemplate
      },
      {
        label: (
          <FormattedMessage
            id="predict.add_loans_google_sheet"
            defaultMessage="Add loans using Google Sheets template"
          />
        ),
        link: getPredictForecastImportUrl(workspaceId, organisationId, forecast?.id || ''),
        id: ChecklistItemIDs.AddLoansGoogleSheetsTemplate
      },
      {
        label: <FormattedMessage id="predict.add_balance_sheet_items" defaultMessage="Add Balance Sheet items" />,
        link: getPredictForecastBalanceSheetUrl(workspaceId, organisationId, forecast?.id || ''),
        id: ChecklistItemIDs.BalanceSheet
      },
      {
        label: <FormattedMessage id="predict.forecast_sales" defaultMessage="Forecast Sales to cover all outgoings" />,
        link: getPredictForecastSalesUrl(workspaceId, organisationId, forecast?.id || ''),
        id: ChecklistItemIDs.Sales
      }
    ];

    if (forecast?.kind === 'live' || (forecast?.kind === 'scenario' && forecast.include_unpaid_invoices)) {
      infos.splice(1, 0, {
        label: (
          <FormattedMessage
            id="predict.review_invoices_bills_payment_dates"
            defaultMessage="Review invoices and bills payment dates"
          />
        ),
        link: getPredictForecastInvoicesUrl(workspaceId, organisationId, forecast?.id || ''),
        id: ChecklistItemIDs.InvoicesBills
      });
    }
    return infos;
  }, [workspaceId, organisationId, forecast]);
};
